import { useEffect, useState, useCallback, useMemo } from "react";
import { Info, Lock, Target, Activity, ArrowDownUp, CalendarDays, Droplet, TrendingDown, TrendingUp } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useProfile } from "../context/ProfileContext";
import { buildStats } from "../components/analysis/stats";
import { PeriodChart, HourlyChart, BandsChart, CarbsInsulinChart } from "../components/analysis/AnalysisCharts";

const RANGES = [
  { id: 1, key: "filter_day" },
  { id: 7, key: "filter_7" },
  { id: 14, key: "filter_14" },
  { id: 30, key: "filter_30" },
];

function Card({ Icon, label, value, sub, testid, accent = "#1A73E8" }) {
  return (
    <div className="df-card p-4 min-w-0" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-semibold text-[#627D98] uppercase tracking-wide break-words">{label}</span>
        <span className="h-7 w-7 shrink-0 rounded-lg flex items-center justify-center" style={{ background: `${accent}1A` }}><Icon size={15} style={{ color: accent }} /></span>
      </div>
      <div className="mt-1.5 font-head text-xl sm:text-2xl font-bold text-[#0D253F] break-words">{value}</div>
      {sub && <div className="text-xs text-[#627D98] mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, sub, children, testid }) {
  return (
    <div className="df-card p-4 sm:p-5 min-w-0" data-testid={testid}>
      <h3 className="font-head font-semibold text-[#0D253F]">{title}</h3>
      {sub && <p className="text-xs text-[#627D98] mt-0.5 mb-3">{sub}</p>}
      {children}
    </div>
  );
}

export default function Analysis() {
  const { t } = useI18n();
  const { activeProfileId } = useProfile();
  const [data, setData] = useState({ g: [], m: [], d: [] });
  const [target, setTarget] = useState(null);
  const [days, setDays] = useState(7);
  const [tab, setTab] = useState("manual");

  const load = useCallback(async () => {
    if (!activeProfileId) return;
    const base = `/profiles/${activeProfileId}`;
    const [g, m, d, tg] = await Promise.all([
      api.get(`${base}/glucose`, { params: { limit: 3000 } }),
      api.get(`${base}/meals`, { params: { limit: 2000 } }),
      api.get(`${base}/doses`, { params: { limit: 2000 } }),
      api.get(`${base}/targets`),
    ]);
    setData({ g: g.data, m: m.data, d: d.data });
    setTarget(tg.data.current);
  }, [activeProfileId]);

  useEffect(() => { load(); }, [load]);

  const st = useMemo(() => buildStats(data.g, data.m, data.d, target, days), [data, target, days]);
  const s = st.summary;
  const u = s.unit;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold text-[#0D253F]">{t("analysis_title")}</h1>
        <p className="text-[#627D98] mt-1">{t("analysis_sub")}</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {RANGES.map((r) => (
          <button key={r.id} onClick={() => setDays(r.id)} data-testid={`range-${r.id}`}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${days === r.id ? "bg-[#1A73E8] text-white" : "bg-white border border-[#E3EFF9] text-[#3B526B]"}`}>
            {t(r.key)}
          </button>
        ))}
      </div>

      <div className="flex gap-2 border-b border-[#E3EFF9] overflow-x-auto">
        <button onClick={() => setTab("manual")} className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap ${tab === "manual" ? "border-[#1A73E8] text-[#1A73E8]" : "border-transparent text-[#627D98]"}`} data-testid="tab-manual">{t("readings")} · Manual</button>
        <button onClick={() => setTab("sensor")} className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px flex items-center gap-1 whitespace-nowrap ${tab === "sensor" ? "border-[#1A73E8] text-[#1A73E8]" : "border-transparent text-[#627D98]"}`} data-testid="tab-sensor"><Lock size={13} /> {t("tir_sensor")}</button>
      </div>

      {tab === "manual" ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card testid="an-target" Icon={Target} label={t("an_target")} value={target ? `${target.lower}–${target.upper}` : "—"} sub={target ? u : t("an_no_target")} accent="#137333" />
            <Card testid="an-count" Icon={Droplet} label={t("readings")} value={s.n} sub={t("an_days_with", { n: s.daysWithReadings })} />
            <Card testid="an-avg" Icon={Activity} label={t("average")} value={s.avg != null ? `${s.avg}` : "—"} sub={u} />
            <Card testid="an-minmax" Icon={ArrowDownUp} label={t("an_minmax")} value={s.n ? `${s.min} / ${s.max}` : "—"} sub={u} />
            <Card testid="an-perday" Icon={CalendarDays} label={t("an_per_day")} value={s.perDay} sub={t("an_readings_day")} />
            <Card testid="an-in" Icon={Target} label={t("within")} value={`${s.in.p}%`} sub={`${s.in.c} ${t("an_readings")}`} accent="#137333" />
            <Card testid="an-below" Icon={TrendingDown} label={t("below")} value={`${s.below.p}%`} sub={`${s.below.c} ${t("an_readings")}`} accent="#C5221F" />
            <Card testid="an-above" Icon={TrendingUp} label={t("above")} value={`${s.above.p}%`} sub={`${s.above.c} ${t("an_readings")}`} accent="#B06000" />
          </div>

          <div className="df-card p-4 sm:p-5">
            <h3 className="font-head font-semibold text-[#0D253F] mb-3">{t("pct_in_target")}</h3>
            <div className="flex items-baseline gap-3 flex-wrap">
              <div className="font-head text-4xl font-extrabold text-[#137333]" data-testid="pct-in-target">{s.in.p}%</div>
              {target && <span className="text-sm text-[#627D98]">{t("an_target")}: {target.lower}–{target.upper} {u}</span>}
            </div>
            <div className="mt-4 flex h-4 rounded-full overflow-hidden bg-[#F0F5FA]">
              <div style={{ width: `${s.below.p}%`, background: "#C5221F" }} />
              <div style={{ width: `${s.in.p}%`, background: "#137333" }} />
              <div style={{ width: `${s.above.p}%`, background: "#B06000" }} />
            </div>
            <div className="flex justify-between text-xs text-[#627D98] mt-2 gap-2">
              <span>{t("below")} {s.below.p}%</span>
              <span>{t("within")} {s.in.p}%</span>
              <span>{t("above")} {s.above.p}%</span>
            </div>
            <p className="text-xs text-[#627D98] mt-4 flex items-start gap-1.5"><Info size={13} className="mt-0.5 shrink-0" /> {t("not_tir")}</p>
          </div>

          <Section testid="sec-period" title={t("an_period_title")} sub={t("an_period_sub")}>
            <PeriodChart points={st.points} target={target} unit={u} days={days} />
          </Section>

          <div className="grid lg:grid-cols-2 gap-5">
            <Section testid="sec-hourly" title={t("an_hourly_title")} sub={t("an_hourly_sub")}>
              <HourlyChart hours={st.hours} target={target} unit={u} />
            </Section>
            <Section testid="sec-bands" title={t("an_bands_title")} sub={t("an_bands_sub")}>
              <BandsChart bands={st.bands} />
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                {st.bands.map((b) => (
                  <div key={b.id} className="flex items-center gap-2 text-xs text-[#3B526B]" data-testid={`band-${b.id}`}>
                    <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: b.color }} />
                    <span className="font-semibold">{b.label}</span>
                    <span className="text-[#627D98] truncate">{b.range}</span>
                    <span className="ml-auto font-semibold">{b.pct}%</span>
                  </div>
                ))}
              </div>
            </Section>
          </div>

          <Section testid="sec-carbs-insulin" title={t("an_daily_title")} sub={t("an_daily_sub")}>
            <CarbsInsulinChart daily={st.daily} />
          </Section>

          <p className="text-xs text-[#627D98] flex items-start gap-1.5"><Info size={13} className="mt-0.5 shrink-0" /> {t("manual_note")}</p>
        </>
      ) : (
        <div className="df-card p-8 text-center">
          <Lock size={28} className="mx-auto text-[#627D98]" />
          <h3 className="font-head font-semibold text-[#0D253F] mt-3">{t("tir_sensor")}</h3>
          <p className="text-sm text-[#627D98] mt-2 max-w-md mx-auto">{t("tir_unavailable")}</p>
        </div>
      )}
    </div>
  );
}
